import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  X,
  Lock,
  Plus,
  Minus,
  ArrowRight,
  AlertTriangle,
  ArrowUpRight,
  ArrowRightLeft,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

// ── Pricing model ────────────────────────────────────────────────────────
const PRICES = { hub: 199, service: 49, location: 49 } as const;
const LIMITS = {
  hub: { min: 1, max: 1 },
  service: { min: 1, max: 20 },
  location: { min: 5, max: 25 },
} as const;

const PRESETS = [
  { id: "starter",     label: "Basic Map Presence",      hub: 1, service: 3, location: 5  },
  { id: "standard",    label: "Serious Map Coverage",    hub: 1, service: 5, location: 8  },
  { id: "domination",  label: "Premium Map Domination",  hub: 1, service: 8, location: 12 },
] as const;

type SiloKey = "hub" | "service" | "location";

// ── DEV: Replace with a real Stripe Checkout Session endpoint that takes
// the configured bundle and returns a hosted checkout URL. Keeping the
// placeholder here so the page can ship as-is.
const CHECKOUT_BASE = "https://checkout.example.com";

function buildCheckoutUrl(state: Record<SiloKey, number>, total: number) {
  return `${CHECKOUT_BASE}?hub=${state.hub}&services=${state.service}&locations=${state.location}&total=${total}`;
}

const fmt = (n: number) => `$${n.toLocaleString()}`;

// ── Animated count-up hook ───────────────────────────────────────────────
function useCountUp(value: number, duration = 220) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const v = Math.round(from + (to - from) * t);
      setDisplay(v);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return display;
}

// ─────────────────────────────────────────────────────────────────────────
export default function WebsitePage() {
  const [state, setState] = useState<Record<SiloKey, number>>({
    hub: 1, service: 5, location: 8,
  });

  const total = useMemo(
    () => state.hub * PRICES.hub + state.service * PRICES.service + state.location * PRICES.location,
    [state],
  );
  const totalAnimated = useCountUp(total);

  const checkoutUrl = buildCheckoutUrl(state, total);
  const checkoutLabel = `Checkout — ${fmt(totalAnimated)}`;

  const step = (k: SiloKey, delta: number) =>
    setState((s) => {
      const next = Math.max(LIMITS[k].min, Math.min(LIMITS[k].max, s[k] + delta));
      return next === s[k] ? s : { ...s, [k]: next };
    });

  const applyPreset = (p: (typeof PRESETS)[number]) =>
    setState({ hub: p.hub, service: p.service, location: p.location });

  const activePreset = PRESETS.find(
    (p) => p.hub === state.hub && p.service === state.service && p.location === state.location,
  );

  // ── JSON-LD blocks ────────────────────────────────────────────────────
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "GBP Silo",
      "description":
        "Engineered local SEO websites built for Google Map Pack ranking using hub-and-spoke topical silos.",
      "areaServed": "United States",
      "priceRange": "$$",
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Local SEO Website Build",
      "provider": { "@type": "ProfessionalService", "name": "GBP Silo" },
      "offers": [
        { "@type": "Offer", "name": "City Hub Page",  "price": "199", "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Service Page",   "price": "49",  "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Location Page",  "price": "49",  "priceCurrency": "USD" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    },
  ];

  // Document title + JSON-LD injection (no react-helmet dep)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Website Services for GBP Maps Ranking — Digital Domination";
    const tags: HTMLScriptElement[] = jsonLd.map((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(block);
      s.dataset.siloLd = "1";
      document.head.appendChild(s);
      return s;
    });
    return () => {
      document.title = prevTitle;
      tags.forEach((t) => t.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="h-full overflow-y-auto bg-[#0A0A0F] text-gray-200 pb-24 md:pb-0"
      data-testid="container-website"
      style={{ fontFamily: "Inter, system-ui, sans-serif" }}
    >
      {/* ── 1. HERO ────────────────────────────────────────────────── */}
      <section id="top" className="relative overflow-hidden border-b border-white/5">
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(59,130,246,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.06) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <Eyebrow>Local SEO • Built for the algorithm</Eyebrow>
              <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
                Your Maps Rank Isn't About Distance Anymore.{" "}
                <span className="text-[#F6642A]">It's About Your Website.</span>
              </h1>
              <p className="mt-6 text-lg text-gray-300 max-w-xl">
                Google's local algorithm changed in 2025.{" "}
                <strong className="text-white">Physical distance is now only 3% of the proximity equation.</strong>{" "}
                The other 97% lives on your website.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#paradigm" data-testid="link-hero-howitworks">
                  <Button
                    variant="outline"
                    className="border-white/15 hover:border-[#F6642A] bg-transparent text-gray-100 font-semibold px-6 py-6 text-base"
                  >
                    See How It Works
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex items-center flex-wrap gap-x-4 gap-y-2 text-xs text-gray-500 font-mono">
                <span>NavBoost</span><span className="text-white/10">/</span>
                <span>BERT</span><span className="text-white/10">/</span>
                <span>Topical authority</span>
              </div>
            </div>
            <HeroDiagram />
          </div>
        </div>
      </section>

      {/* ── 2. PARADIGM SHIFT ──────────────────────────────────────── */}
      <Section id="paradigm" eyebrow="The pattern interrupt" title="The Paradigm Shift" muted>
        <div className="grid md:grid-cols-2 gap-5">
          <Card className="border-white/10 bg-[#11161D]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="secondary" className="font-mono text-[10px] uppercase tracking-widest">DEPRECATED</Badge>
                <h3 className="text-xl font-bold text-white">Old Model</h3>
              </div>
              <ol className="space-y-3 text-gray-300">
                <li><span className="font-mono text-[#F6642A] mr-2">01</span>User searches.</li>
                <li><span className="font-mono text-[#F6642A] mr-2">02</span>Google finds the closest businesses.</li>
                <li><span className="font-mono text-[#F6642A] mr-2">03</span><strong className="text-white">Closest wins.</strong></li>
              </ol>
              <p className="mt-5 text-sm text-gray-500">
                Distance was the dominant signal. Most local SEO advice is still stuck here.
              </p>
            </CardContent>
          </Card>
          <Card className="border-[#F6642A]/40 bg-gradient-to-b from-[#F6642A]/[0.05] to-transparent">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Badge className="font-mono text-[10px] uppercase tracking-widest bg-[#F6642A] text-white">{new Date().getFullYear()}</Badge>
                <h3 className="text-xl font-bold text-white">New Model</h3>
              </div>
              <ol className="space-y-3 text-gray-300">
                <li><span className="font-mono text-[#0081DF] mr-2">01</span>User searches.</li>
                <li><span className="font-mono text-[#0081DF] mr-2">02</span>Google evaluates businesses in a 1–2 mile radius.</li>
                <li><span className="font-mono text-[#0081DF] mr-2">03</span>Evaluates vectors, entity trust, content relevance.</li>
                <li><span className="font-mono text-[#0081DF] mr-2">04</span><strong className="text-white">Most relevant wins — even if farther away.</strong></li>
              </ol>
              <p className="mt-5 text-sm text-gray-300">
                Relevance now overrides distance decay. That's the whole game.
              </p>
            </CardContent>
          </Card>
        </div>

        <blockquote className="mt-12 border-l-4 border-[#0081DF] pl-6 max-w-3xl mx-auto">
          <p className="text-xl md:text-2xl font-semibold leading-snug text-white">
            "Physical distance is now only <span className="text-[#0081DF]">3%</span> of the proximity equation. If your relevance score is high enough, it overrides distance decay. <span className="text-[#0081DF]">That's the cheat code.</span>"
          </p>
        </blockquote>
      </Section>

      {/* ── 3. WHY THE LANDING PAGE IS THE WHOLE GAME ─────────────── */}
      <Section
        id="hub"
        eyebrow="The emotional core"
        title="Why The Landing Page Is The Whole Game"
        intro={
          <>Your <span className="font-mono text-[#F6642A]">/las-vegas/</span> City Hub Page is the top of the pyramid. It connects to your GBP and inherits the authority of every supporting page below it.</>
        }
      >
        <div className="grid md:grid-cols-3 gap-5">
          <FeatureCard
            n="01 / Concentrate"
            title="Topical authority concentrator"
            body={<>Every service and every neighborhood page links <strong className="text-gray-200">up to this single page</strong>. Authority compounds in one place instead of scattering.</>}
          />
          <FeatureCard
            n="02 / Anchor"
            title="Geographic entity anchor"
            body={<>Tells Google what city you serve in a way the algorithm can <strong className="text-gray-200">verify</strong> — not just claim.</>}
          />
          <FeatureCard
            n="03 / Feed"
            title="Direct line to your GBP"
            body={<>Relevance flows from the hub straight into your Business Profile, <strong className="text-gray-200">expanding the radius you rank in</strong>.</>}
          />
        </div>

        <Callout variant="danger" className="mt-10">
          <strong className="text-white">Don't</strong> let an agency build you{" "}
          <span className="font-mono text-red-400">/city-service/</span> pages.
          They <strong className="text-white">dilute authority</strong>. One City Hub. All services link to it. <strong>Period.</strong>
        </Callout>
      </Section>

      {/* ── 4. HUB & SPOKE SILO ───────────────────────────────────── */}
      <Section id="silo" eyebrow="The diagram" title="The Hub & Spoke Silo" muted>
        <Card className="border-white/10 bg-[#11161D]">
          <CardContent className="p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
              <div className="rounded-lg border border-white/10 bg-[#161C25] p-4">
                <div className="font-mono text-[11px] text-gray-500 mb-1">Tier 1 · Service</div>
                <div className="font-bold text-white">Non-Geo Service Page</div>
                <div className="font-mono text-[#F6642A] text-sm mt-1">/ac-repair/</div>
                <div className="font-mono text-[10px] text-gray-500 mt-2 space-y-0.5">
                  <div>↳ links UP to City Hub</div>
                  <div>↳ links ACROSS to other Service Pages</div>
                </div>
              </div>
              <div className="rounded-lg border border-[#0081DF]/40 bg-[#0081DF]/5 p-4">
                <div className="font-mono text-[11px] text-[#0081DF] mb-1">Tier 1 · Hub</div>
                <div className="font-bold text-white">City Hub Page</div>
                <div className="font-mono text-[#0081DF] text-sm mt-1">/las-vegas/</div>
                <div className="font-mono text-[10px] text-gray-500 mt-2 space-y-0.5">
                  <div>↳ links DOWN to ALL Service Pages</div>
                  <div>↳ links DOWN to ALL Neighborhood Pages</div>
                  <div className="text-[#0081DF]/80">↳ feeds your GBP</div>
                </div>
              </div>
            </div>

            <div className="flex justify-center mb-2">
              <svg width="220" height="40" viewBox="0 0 220 40" aria-hidden="true">
                <path d="M 20 0 Q 20 20 110 20 Q 110 20 200 20 Q 200 20 200 40" stroke="#F6642A" strokeWidth="1.5" fill="none" strokeDasharray="4 4" />
                <path d="M 200 0 Q 200 20 110 20 Q 110 20 110 40" stroke="#0081DF" strokeWidth="1.5" fill="none" strokeDasharray="4 4" />
              </svg>
            </div>

            <div className="font-mono text-[11px] text-gray-500 mb-2 text-center">Tier 2 · Neighborhood Spokes</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {["/imperial-point/", "/victoria-park/", "/coral-ridge/", "/rio-vista/", "/las-olas/"].map((slug) => (
                <div
                  key={slug}
                  className="rounded-md border border-white/10 bg-[#161C25] px-3 py-2 font-mono text-xs text-gray-300 text-center"
                >
                  {slug}
                </div>
              ))}
            </div>
            <div className="mt-3 font-mono text-[10px] text-gray-500 text-center space-y-0.5">
              <div>↳ each links UP to the City Hub</div>
              <div>↳ each links ACROSS to 2–3 nearby neighborhoods + 2–3 Service Pages</div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-12 max-w-3xl mx-auto text-gray-300 space-y-4 text-[15px] leading-relaxed">
          <p>
            Think of the silo as a <strong className="text-white">three-tier pyramid of relevance</strong>. At the bottom, every Neighborhood Page proves you actually serve that micro-area — with local landmarks, real photos, microclimate language, and FAQs that match how locals talk. Each one of those pages then passes a clean, single vote of authority{" "}
            <span className="font-mono text-[#F6642A]">up</span> the silo and{" "}
            <span className="font-mono text-[#F6642A]">across</span> to its sibling spokes.
          </p>
          <p>
            All of those votes converge on one place: the <strong className="text-white">City Hub Page</strong>. The hub doesn't try to rank for every long-tail keyword — its job is to become the <strong className="text-white">single most relevant page on the internet for "[your service] in [your city]"</strong>, then route that gravity directly into your Google Business Profile. That's the part competitors miss. They build pages. We build a circuit.
          </p>
          <p>
            The Non-Geo Service Pages run in parallel as a topical reinforcement layer. They prove subject-matter depth without competing with the hub for geographic authority — so when Google's local algorithm asks "<em>is this business actually the most relevant result for this query in this area?</em>", every page in your silo answers <strong className="text-white">yes</strong> from a different angle.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <FeatureCard
            n="flow_01"
            title="Spokes feed the hub"
            body={<>Every Neighborhood and Service page links <strong className="text-gray-200">up</strong> to the City Hub. Authority compounds in one place instead of leaking across dozens of unrelated URLs.</>}
          />
          <FeatureCard
            n="flow_02"
            title="The hub feeds your GBP"
            body={<>Once the hub is the strongest page on your domain for the city, that <strong className="text-gray-200">topical relevance flows directly into your Business Profile</strong> — expanding the radius you rank in.</>}
          />
          <FeatureCard
            n="flow_03"
            title="Neighbors reinforce neighbors"
            body={<>Each Neighborhood Page links across to <strong className="text-gray-200">2–3 nearby spokes</strong> — never all of them. That mirrors how Google models real geographic relationships, not a flat list.</>}
          />
        </div>

        <p className="mt-10 text-center text-gray-400 max-w-2xl mx-auto text-sm">
          This isn't a sitemap. It's a <strong className="text-white">topographical community Google can read</strong>. Each neighborhood page boosts your visibility in that exact area — and the whole structure compounds the longer it sits live.
        </p>
      </Section>

      {/* ── 5. LINKING FORMULA ────────────────────────────────────── */}
      <Section
        id="formula"
        eyebrow="The proof"
        title={
          <>
            <span className="font-mono text-[#F6642A]">1 UP</span> +{" "}
            <span className="font-mono text-[#F6642A]">2–3 ACROSS</span> ={" "}
            <span className="text-[#0081DF]">Money</span>
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-5">
          <Card className="border-white/10 bg-[#11161D]">
            <CardContent className="p-6">
              <div className="font-mono text-[11px] text-gray-500 mb-2">Each Neighborhood Page</div>
              <h3 className="text-xl font-bold mb-4 text-white">Spoke linking pattern</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex gap-3"><ArrowUpRight className="w-4 h-4 text-[#0081DF] mt-1 shrink-0" /><span><strong className="text-white">1 UP</strong> → City Hub Page (first paragraph, above the fold)</span></li>
                <li className="flex gap-3"><ArrowRightLeft className="w-4 h-4 text-[#F6642A] mt-1 shrink-0" /><span><strong className="text-white">2–3 ACROSS</strong> → Non-Geo Service Pages</span></li>
                <li className="flex gap-3"><ArrowRightLeft className="w-4 h-4 text-[#F6642A] mt-1 shrink-0" /><span><strong className="text-white">2–3 ACROSS</strong> → Neighboring neighborhoods (not all of them)</span></li>
              </ul>
            </CardContent>
          </Card>
          <Card className="border-[#0081DF]/40 bg-[#0081DF]/5">
            <CardContent className="p-6">
              <div className="font-mono text-[11px] text-[#0081DF] mb-2">City Hub Page</div>
              <h3 className="text-xl font-bold mb-4 text-white">Hub linking pattern</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex gap-3"><ArrowRight className="w-4 h-4 text-[#0081DF] mt-1 shrink-0" /><span>Links to <strong className="text-white">ALL</strong> Non-Geo Service Pages</span></li>
                <li className="flex gap-3"><ArrowRight className="w-4 h-4 text-[#0081DF] mt-1 shrink-0" /><span>Links to <strong className="text-white">ALL</strong> Neighborhood Pages</span></li>
              </ul>
              <div className="mt-4 font-mono text-[11px] text-[#0081DF]/80">↳ becomes the gravitational center</div>
            </CardContent>
          </Card>
        </div>

        <Callout variant="danger" className="mt-8" icon={<X className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />}>
          <strong className="text-white">Sidebars stuffed with service links? Footer link dumps?</strong> That's lazy SEO from 2015. It doesn't pass juice. We build links into the <strong className="text-white">body content</strong> where Google actually counts them.
        </Callout>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <WhyCard n="why_01" body={<>AI doesn't inherently know "Providence" is in Las Vegas — your <strong className="text-white">linking structure</strong> tells Google.</>} />
          <WhyCard n="why_02" body={<>Each neighborhood page increases your coverage of that specific grid square.</>} />
          <WhyCard n="why_03" body={<>Authority compounds upward to the City Hub, then <strong className="text-white">flows to your GBP</strong>.</>} />
        </div>
      </Section>

      {/* ── 6. WHAT YOU ACTUALLY GET ──────────────────────────────── */}
      <Section id="deliverable" eyebrow="The deliverable" title="What You Actually Get" intro="Every line is tied to the algorithm. No filler." muted>
        <ul className="grid md:grid-cols-2 gap-4">
          {DELIVERABLES.map((d, i) => (
            <li key={i} className="rounded-lg border border-white/10 bg-[#11161D] p-4 flex gap-3">
              <Check className="w-5 h-5 text-[#0081DF] mt-0.5 shrink-0" />
              <div className="text-gray-300">{d}</div>
            </li>
          ))}
        </ul>
      </Section>

      {/* ── 6.5 PRICING & BUNDLE BUILDER ──────────────────────────── */}
      <Section
        id="pricing"
        eyebrow="Pricing"
        title={<>Build Your Silo. <span className="text-[#F6642A]">One Checkout.</span></>}
        intro="Each piece is priced à la carte so you can see exactly what you're paying for. But the silo only works as a system — that's why the checkout button bundles everything into one purchase."
      >
        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-5 mb-12">
          <PricingCard
            tag="Required" tagColor="lime" qty="x1"
            title="City Hub" subtitle="The top of the pyramid."
            price="$199"
            bullets={[
              "Topical center of gravity for your city",
              "Links to every service + every neighborhood",
              "Engineered to feed authority to your GBP",
              "BERT-optimized headers, FAQ schema, JSON-LD",
            ]}
            footer="Every silo needs exactly one."
            highlight
          />
          <PricingCard
            tag="Recommended" tagColor="blue" qty="per service"
            title="Service Page" subtitle="Topical authority that flows across the silo."
            price="$49" priceSuffix="/each"
            bullets={[
              "One page per service you offer",
              "No city in the URL — keeps authority concentrated",
              "Linked across from every neighborhood page",
              "Verb-noun pairs tuned for BERT contextual vectors",
            ]}
            footer="1 per service line."
          />
          <PricingCard
            tag="Recommended" tagColor="blue" qty="per neighborhood"
            title="Location Page" subtitle="One page = one neighborhood covered."
            price="$49" priceSuffix="/each"
            bullets={[
              "Targets a specific neighborhood inside your city",
              "80% unique — microclimate, landmarks, local FAQs",
              "1 UP + 2–3 ACROSS internal linking pre-built",
              "Real photos from that area, not AI-generated",
            ]}
            footer="5–10 per city hub."
          />
        </div>

        {/* Bundle Builder */}
        <Card className="border-white/10 bg-[#11161D]/60 rounded-2xl">
          <CardContent className="p-6 md:p-8">
            {/* Suggested chips */}
            <div className="mb-6">
              <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500 mb-3">Suggested bundles</div>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => {
                  const presetTotal = p.hub * PRICES.hub + p.service * PRICES.service + p.location * PRICES.location;
                  const active = activePreset?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyPreset(p)}
                      data-testid={`button-preset-${p.id}`}
                      className={`px-3.5 py-2 rounded-full text-sm border transition-colors ${
                        active
                          ? "border-[#0081DF]/60 bg-[#0081DF]/10 text-[#0081DF]"
                          : "border-white/10 bg-[#11161D] text-gray-200 hover:border-[#F6642A]"
                      }`}
                    >
                      {p.label} · {fmt(presetTotal)}
                      {p.id === "standard" && (
                        <span className="ml-2 font-mono text-[9px] uppercase tracking-widest bg-[#0081DF]/20 text-[#0081DF] px-1.5 py-0.5 rounded">
                          Most Popular
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Calculator rows */}
            <div className="space-y-4">
              <CalcRow
                title="City Hub Page"
                meta="$199 · required"
                qty={state.hub}
                lineTotal={state.hub * PRICES.hub}
                locked
                onDec={() => {}}
                onInc={() => {}}
              />
              <CalcRow
                title="Service Pages"
                meta={`$49 each · min ${LIMITS.service.min} · max ${LIMITS.service.max}`}
                qty={state.service}
                lineTotal={state.service * PRICES.service}
                onDec={() => step("service", -1)}
                onInc={() => step("service",  1)}
                ariaName="Service Pages"
              />
              <CalcRow
                title="Location Pages"
                meta={`$49 each · min ${LIMITS.location.min} · max ${LIMITS.location.max}`}
                qty={state.location}
                lineTotal={state.location * PRICES.location}
                onDec={() => step("location", -1)}
                onInc={() => step("location",  1)}
                ariaName="Location Pages"
              />
            </div>

            {/* Total + Checkout */}
            <div className="mt-8 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-5 border-t border-white/10 pt-6">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500 mb-1">Bundle total</div>
                <div
                  data-testid="text-bundle-total"
                  className="text-4xl md:text-5xl font-extrabold text-white tabular-nums transition-transform"
                >
                  {fmt(totalAnimated)}
                </div>
                <div className="text-[11px] text-gray-500 mt-2 max-w-md">
                  Fewer than <strong className="text-gray-300">5 neighborhood pages</strong> won't move the needle enough to matter. We won't sell you a silo that can't rank.
                </div>
              </div>
              <div className="flex flex-col items-stretch md:items-end gap-2 min-w-[260px]">
                <a
                  href={checkoutUrl}
                  data-testid="link-checkout"
                  className="inline-flex items-center justify-center bg-[#F6642A] hover:bg-[#FF7A45] text-white font-bold text-lg px-6 py-4 rounded-md w-full md:w-auto transition-colors"
                >
                  {checkoutLabel}
                </a>
                <p className="text-[11px] text-gray-500 text-center md:text-right">
                  One checkout. Whole silo. We start the build the same day payment clears.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* ── 7. RADIUS EXPANSION PROMISE ───────────────────────────── */}
      <section className="border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <Eyebrow>The closer</Eyebrow>
          <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-white">
            This Is How Businesses Rank <span className="text-[#F6642A]">10–20 Miles</span> From Their Pin.
          </h2>
          <p className="mt-6 text-lg text-gray-300">
            When your relevance score is high enough, it overrides distance decay. A properly built website doesn't just rank you for your address — it{" "}
            <strong className="text-white">expands your map coverage across every neighborhood you've targeted</strong>.
          </p>
          <p className="mt-3 font-mono text-sm text-[#0081DF]">NavBoost + semantic agreement = radius expansion.</p>
        </div>
      </section>

      {/* ── 8. FAQ ────────────────────────────────────────────────── */}
      <Section id="faq" eyebrow="Questions" title="FAQ">
        <div className="space-y-4 max-w-3xl mx-auto">
          {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
        </div>
      </Section>

      {/* ── 9. FINAL CTA ──────────────────────────────────────────── */}
      <section id="final-cta" className="border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-center">
            <Eyebrow>Final word</Eyebrow>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-white">
              Stop Paying For SEO That <span className="text-[#F6642A]">Ignores The Algorithm.</span>
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-stretch">
              <a
                href={checkoutUrl}
                data-testid="link-checkout-final"
                className="inline-flex items-center justify-center bg-[#F6642A] hover:bg-[#FF7A45] text-white font-bold text-lg px-6 py-4 rounded-md transition-colors"
              >
                Build My Silo — {fmt(totalAnimated)}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#0081DF]" />
            <span className="font-bold text-gray-300">GBP Silo</span>
            <span className="font-mono">/v.2025</span>
          </div>
          <div>Built for the algorithm. Not for the brochure.</div>
        </div>
      </footer>

      {/* ── Sticky bottom CTA (mobile only) ───────────────────────── */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#0A0A0F]/95 backdrop-blur p-3">
        <a
          href={checkoutUrl}
          data-testid="link-checkout-mobile"
          className="block w-full text-center bg-[#F6642A] hover:bg-[#FF7A45] text-white font-bold py-3 rounded-md transition-colors"
        >
          {checkoutLabel}
        </a>
      </div>
    </div>
  );
}

// ─── Helpers / sub-components ────────────────────────────────────────────
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[11px] uppercase tracking-widest text-[#0081DF]">{children}</div>
  );
}

function Section({
  id,
  eyebrow,
  title,
  intro,
  children,
  muted,
}: {
  id?: string;
  eyebrow: string;
  title: React.ReactNode;
  intro?: React.ReactNode;
  children: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <section id={id} className={`border-t border-white/5 ${muted ? "bg-white/[0.02]" : ""}`}>
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-white">{title}</h2>
          {intro && <p className="mt-4 text-gray-400 max-w-2xl mx-auto">{intro}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

function FeatureCard({ n, title, body }: { n: string; title: string; body: React.ReactNode }) {
  return (
    <Card className="border-white/10 bg-[#11161D]">
      <CardContent className="p-6">
        <div className="font-mono text-xs text-[#F6642A] mb-2">{n}</div>
        <h3 className="font-bold text-lg mb-2 text-white">{title}</h3>
        <p className="text-gray-400 text-sm">{body}</p>
      </CardContent>
    </Card>
  );
}

function WhyCard({ n, body }: { n: string; body: React.ReactNode }) {
  return (
    <Card className="border-white/10 bg-[#11161D]/60">
      <CardContent className="p-5">
        <div className="font-mono text-xs text-[#F6642A] mb-2">// {n}</div>
        <p className="text-gray-300 text-sm">{body}</p>
      </CardContent>
    </Card>
  );
}

function Callout({
  variant = "info",
  icon,
  className = "",
  children,
}: {
  variant?: "info" | "danger";
  icon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  const cls =
    variant === "danger"
      ? "border-red-500/40 bg-red-500/5"
      : "border-[#F6642A]/40 bg-[#F6642A]/5";
  return (
    <div className={`rounded-xl border ${cls} p-5 flex items-start gap-4 ${className}`}>
      {icon ?? <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />}
      <p className="text-gray-200">{children}</p>
    </div>
  );
}

function CalcRow({
  title,
  meta,
  qty,
  lineTotal,
  onDec,
  onInc,
  locked,
  ariaName,
}: {
  title: string;
  meta: string;
  qty: number;
  lineTotal: number;
  onDec: () => void;
  onInc: () => void;
  locked?: boolean;
  ariaName?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-[#11161D] p-4" data-testid={`row-calc-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-white flex items-center gap-2">
          {title}
          {locked && (
            <span className="font-mono text-[10px] text-gray-500 inline-flex items-center gap-1" title="Every silo requires exactly one City Hub.">
              <Lock className="w-3 h-3" /> locked
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500">{meta}</div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onDec}
          disabled={locked}
          aria-label={`Decrease ${ariaName ?? title}`}
          data-testid={`button-dec-${title.toLowerCase().replace(/\s+/g, '-')}`}
          className="w-9 h-9 rounded-md bg-white/5 border border-white/10 hover:border-[#F6642A] hover:bg-white/10 text-gray-100 inline-flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Minus className="w-4 h-4" />
        </button>
        <div className="w-12 text-center font-mono font-bold text-lg text-white tabular-nums" aria-live="polite" data-testid={`text-qty-${title.toLowerCase().replace(/\s+/g, '-')}`}>
          {qty}
        </div>
        <button
          type="button"
          onClick={onInc}
          disabled={locked}
          aria-label={`Increase ${ariaName ?? title}`}
          data-testid={`button-inc-${title.toLowerCase().replace(/\s+/g, '-')}`}
          className="w-9 h-9 rounded-md bg-white/5 border border-white/10 hover:border-[#F6642A] hover:bg-white/10 text-gray-100 inline-flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="w-24 text-right font-mono font-semibold text-white" data-testid={`text-line-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        {fmt(lineTotal)}
      </div>
    </div>
  );
}

function PricingCard({
  tag, tagColor, qty, title, subtitle, price, priceSuffix, bullets, footer, highlight,
}: {
  tag: string;
  tagColor: "lime" | "blue";
  qty: string;
  title: string;
  subtitle: string;
  price: string;
  priceSuffix?: string;
  bullets: string[];
  footer: string;
  highlight?: boolean;
}) {
  const tagCls = tagColor === "lime" ? "text-[#0081DF]" : "text-[#F6642A]";
  const bullColor = highlight ? "text-[#0081DF]" : "text-[#F6642A]";
  return (
    <Card className={`flex flex-col ${highlight ? "border-[#0081DF]/40 bg-[#0081DF]/5" : "border-white/10 bg-[#11161D]"}`}>
      <CardContent className="p-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-3">
          <span className={`font-mono text-[10px] uppercase tracking-widest ${tagCls}`}>{tag}</span>
          <span className="font-mono text-[10px] text-gray-500">{qty}</span>
        </div>
        <h3 className="text-xl font-bold text-white">{title}</h3>
        <p className="text-sm text-gray-400">{subtitle}</p>
        <div className="my-5 flex items-baseline gap-1">
          <span className="text-3xl sm:text-4xl font-extrabold text-white">{price}</span>
          {priceSuffix && <span className="text-gray-500 text-sm">{priceSuffix}</span>}
        </div>
        <ul className="space-y-2 text-sm text-gray-300 flex-1">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className={bullColor}>{highlight ? "✓" : "→"}</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <p className="mt-5 text-[11px] text-gray-500">{footer}</p>
      </CardContent>
    </Card>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-lg border bg-[#11161D] p-5 ${open ? "border-[#F6642A]/40" : "border-white/10"}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`button-faq-${q.slice(0, 24).toLowerCase().replace(/\s+/g, '-')}`}
        className="w-full flex justify-between items-center text-left font-semibold text-white"
      >
        <span>{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#F6642A] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="mt-3 text-gray-400">{a}</p>}
    </div>
  );
}

function HeroDiagram() {
  return (
    <div className="relative h-72 md:h-96 rounded-xl border border-white/10 bg-[#11161D]/60 overflow-hidden">
      <style>{`
        @keyframes silo-ring-pulse {
          0%   { transform: scale(0.4); opacity: 0; }
          20%  { opacity: 0.9; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .silo-ring { transform-origin: 200px 200px; transform-box: fill-box; animation: silo-ring-pulse 4s ease-out infinite; }
        .silo-ring.d1 { animation-delay: 1s; }
        .silo-ring.d2 { animation-delay: 2s; }
        .silo-ring.d3 { animation-delay: 3s; }
      `}</style>
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" aria-hidden="true">
        <defs>
          <pattern id="silo-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1F2731" strokeWidth="1" />
          </pattern>
          <radialGradient id="silo-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F6642A" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#F6642A" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="400" height="400" fill="url(#silo-grid)" />
        <circle cx="200" cy="200" r="180" fill="url(#silo-glow)" />
        <g>
          <circle className="silo-ring"    cx="200" cy="200" r="40" fill="none" stroke="#F6642A" strokeWidth="2" />
          <circle className="silo-ring d1" cx="200" cy="200" r="40" fill="none" stroke="#F6642A" strokeWidth="2" />
          <circle className="silo-ring d2" cx="200" cy="200" r="40" fill="none" stroke="#0081DF" strokeWidth="2" />
          <circle className="silo-ring d3" cx="200" cy="200" r="40" fill="none" stroke="#0081DF" strokeWidth="2" />
        </g>
        <g stroke="#F6642A" strokeWidth="1" fill="none" opacity="0.35">
          {[120,160,200,240].map((x) => (<rect key={`t${x}`} x={x} y="120" width="40" height="40" />))}
          {[120,240].map((x) => (<rect key={`m1${x}`} x={x} y="160" width="40" height="40" />))}
          {[120,240].map((x) => (<rect key={`m2${x}`} x={x} y="200" width="40" height="40" />))}
          {[120,160,200,240].map((x) => (<rect key={`b${x}`} x={x} y="240" width="40" height="40" />))}
        </g>
        <g transform="translate(200,200)">
          <circle r="10" fill="#0081DF" />
          <circle r="4" fill="#0A0A0F" />
        </g>
      </svg>
      <div className="absolute bottom-3 left-3 font-mono text-[10px] text-gray-500 bg-[#0A0A0F]/80 border border-white/10 px-2 py-1 rounded">
        pin → expanding coverage
      </div>
    </div>
  );
}

// ─── Content ─────────────────────────────────────────────────────────────
const DELIVERABLES: React.ReactNode[] = [
  <><strong className="text-white">City Hub Page</strong> engineered as the topical center of gravity.</>,
  <><strong className="text-white">5–10 Neighborhood Pages</strong> per city, each targeting its own neighborhood grid.</>,
  <><strong className="text-white">Non-Geo Service Pages</strong> that pass topical authority across the silo.</>,
  <>Hand-built internal linking using the <span className="font-mono text-[#F6642A]">1 UP + 2–3 ACROSS</span> formula.</>,
  <><strong className="text-white">BERT-optimized on-page content</strong> — service keywords positioned within <span className="font-mono">2 tokens</span> of geographic terms, surrounded by high-probability verb-noun pairs.</>,
  <><strong className="text-white">FAQ schema on every page</strong> (triggers website-mention justifications in the Map Pack).</>,
  <><strong className="text-white">80% unique content</strong> per neighborhood — microclimate, landmarks, local cost data, real photos from the area.</>,
  <>Bulk deployment via <span className="font-mono text-[#F6642A]">WP All Import</span> — full silo live in <strong className="text-white">days, not months</strong>.</>,
];

const FAQS = [
  { q: "I already have a website. Do I need a new one?",                a: "Probably not a new one. We restructure what you have into a hub-and-spoke silo. Most sites are a flat pile of pages with no topical hierarchy." },
  { q: "How many neighborhood pages do I need?",                        a: "5–10 per city hub. More isn't better. Thin content hurts more than it helps." },
  { q: "Why not just build city + service combo pages?",                a: "Because it dilutes authority across dozens of pages instead of concentrating it in one City Hub. We've tested both. The hub model wins every time." },
  { q: "How long until I see movement in the Map Pack?",                a: "First Map Pack movement usually shows in 3–6 weeks. Full radius expansion is a 90-day arc." },
  { q: "Do you guarantee rankings?",                                    a: "No one credible does. We guarantee the build matches the algorithm. The rankings follow." },
];
